





























































































































































import Vue, { PropType } from 'vue';
import KintaiSearchableSelect from '@/components/util/KintaiSearchableSelect.vue';
import NewVariableInput from '@/components/util/NewVariableInput.vue';
import DatePicker from 'vuejs-datepicker';
import { ja } from 'vuejs-datepicker/dist/locale';
import { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';
import { LaborCalendarEventKind } from '@/class/models/typed/labor';
import BaseTimeInput from '@/components/util/BaseTimeInput.vue';
import * as validation from '@/personal/validation';

function createDefaultLocal() {
  return {
    LaborCalendarEventKind,
    kind: LaborCalendarEventKind.WORK,
    absenceKind: 'paidLeave',
    paidLeaveKind: 'full-day',
    start: '09:00',
    end: '18:00',
    taskId: undefined,
    timeOffId: undefined,
    specialLeaveId: undefined,
    timeOffStart: undefined,
    timeOffEnd: undefined,
    description: '',
  };
}

const formatYmdSlash = (date: Date): string => {
  if (!date) {
    return '';
  }
  const y = date.getFullYear();
  const m = ('0' + (date.getMonth() + 1)).slice(-2);
  const d = ('0' + date.getDate()).slice(-2);
  return `${y}/${m}/${d}`;
};

interface IOption {
  id: number;
  value: number;
  text: string;
}

interface ReportEvent {
  start: string;
  end: string;
}

/** data() が返すオブジェクトの型 */
interface Data {
  ja: typeof ja;
  timeOffKindError: string;
  timeError: string;
  periodError: string;
  descriptionError: string;
  taskError: string;
  local: {
    kind: string;
    absenceKind: string;
    paidLeaveKind: string;
    start: string;
    end: string;
    taskId?: number;
    timeOffId?: number;
    specialLeaveId?: number;
    timeOffStart?: Date;
    timeOffEnd?: Date;
    description: string;
  };
  descriptionItemBase: any;
  LaborCalendarEventKind: typeof LaborCalendarEventKind;
  formError: string;
}

/** methods の型 */
interface Methods {
  resetErrors(): void;
  datePickerFormat(date: Date): string;
  validateTime(): boolean;
  validatePeriod(mode?: string): boolean;
  validateDescription(): boolean;
  validateTask(): boolean;
  toMinutes(hhmm: string): number;
  hasOverlapWithOthers(start: string, end: string): boolean;
  validateAll(): boolean;
  submit(kind: 'update' | 'add-another'): void;
  onUpdate(): void;
  onAddAnother(): void;
  validateTimeOffKind(): boolean;
  hasAnyAttendance(): boolean;
  validateAbsenceAvailability(): boolean;
  getMaxEndTime(): string | null;
}

/** computed の型 */
interface Computed {
  isWork: boolean;
  isAbsence: boolean;
  isTimeOffAbsence: boolean;
  isPaidHalfDay: boolean;
  showPaidLeaveKind: boolean;
  showTime: boolean;
  descriptionLabel: string;
  descriptionItem: any;
  selectedDateAsDate: Date | null;
  placeHolder: string;
  displayTaskOptions: IOption[];
  descriptionServerError: string;
}

/** props の型 */
interface Props {
  visible: boolean;
  taskOptions: IOption[] | undefined;
  timeOffOptions: IOption[] | undefined;
  dayEvents: ReportEvent[];
  editForm?: any;
  minTime: string;
  maxTime: string;
  initialForm?: any;
  serverErrors: {};
  selectedDate: string;
  originalTaskId: number;
  originalTaskName: string;
  modalOpenedBy: 'new' | 'edit' | '';
}

export default Vue.extend({
  name: 'LaborRegistWorkReport',
  components: {
    KintaiSearchableSelect,
    NewVariableInput,
    DatePicker,
    BaseTimeInput,
  },
  props: {
    selectedDate: String,
    visible: { type: Boolean, default: false },
    taskOptions: Array as PropType<IOption[]>,
    timeOffOptions: Array as PropType<IOption[]>,
    dayEvents: {
      type: Array as PropType<ReportEvent[]>,
      default: () => [],
    },
    editForm: {
      type: Object as PropType<any | undefined>,
      default: () => undefined,
    },
    initialForm: Object,
    minTime: { type: String, default: '08:00' },
    maxTime: { type: String, default: '22:00' },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
    originalTaskId: {
      type: Number,
      default: null,
    },
    originalTaskName: {
      type: String,
      default: '',
    },
    modalOpenedBy: {
      type: String as PropType<'new' | 'edit' | ''>,
      default: '',
    },  
  },
  data() {
    const today = new Date();
    const defaultLocal = createDefaultLocal();
    return {
      formError: '',
      LaborCalendarEventKind,
      ja,
      timeOffKindError: '',
      local: this.editForm
        ? { ...defaultLocal, ...this.editForm }
        : this.initialForm
        ? { ...defaultLocal, ...this.initialForm }
        : defaultLocal,
      timeError: '',
      periodError: '',
      descriptionError: '',
      taskError: '',
      // NewVariableInput 用の item 定義
      descriptionItemBase: {
        type: 'textarea',
        name: '',
        required: false,
        max: 256,
        error: '',
      } as any,
    };
  },
  watch: {
    'local.description'() {
      this.descriptionError = '';
    },
    // 期間フィールドの変更を監視
    'local.timeOffStart'(val) {
      this.validatePeriod('live');
    },
    'local.timeOffEnd'() {
      this.validatePeriod('live');
    },
    'local.specialLeaveId'() {
      this.validateTimeOffKind();
    },
    'local.kind'() {
      this.local.description = '';
      this.resetErrors();
    },
    'local.absenceKind'() {
      this.resetErrors();
    },
    'local.paidLeaveKind'() {
      this.resetErrors();
    },
    editForm: {
      immediate: true,
      handler(newVal) {
        const base = createDefaultLocal();
        this.local = newVal ? { ...base, ...newVal } : base;
      },
    },
    initialForm: {
      immediate: true,
      handler(newVal) {
        if (!this.editForm && newVal) {
          const base = createDefaultLocal();
          this.local = { ...base, ...newVal };
        }
      },
    },
    serverErrors: {
      immediate: true,
      handler(v) {
        if (!v) {
          return;
        }
        if (v.periodError) {
          this.periodError = v.periodError;
        }
        if (v.timeError) {
          this.timeError = v.timeError;
        }
        if (v.taskError) {
          this.taskError = v.taskError;
        }
        if (v.descriptionError) {
          this.descriptionError = v.descriptionError;
        }

        if (v.periodError) {
          if (this.isTimeOffAbsence) {
            this.periodError = v.periodError;
          } else {
            this.formError = v.periodError;
          }
        }
      },
    },
  },
  computed: {
    descriptionServerError(): string {
      return this.descriptionItemBase.error ? '' : this.descriptionError;
    },
    isWork(): boolean {
      return this.local.kind === LaborCalendarEventKind.WORK;
    },
    isAbsence(): boolean {
      return this.local.kind === LaborCalendarEventKind.ABSENCE;
    },
    isTimeOffAbsence(): boolean {
      return this.isAbsence && this.local.absenceKind === 'timeOff';
    },
    isPaidHalfDay(): boolean {
      return this.isAbsence && this.local.absenceKind === 'paidLeave' && this.local.paidLeaveKind === 'half-day';
    },
    showPaidLeaveKind(): boolean {
      return this.isAbsence && this.local.absenceKind === 'paidLeave';
    },
    showTime(): boolean {
      // 出勤 or 遅刻・早退 or 有給半休のときに時間帯を表示
      if (!this.isAbsence) {
        return true;
      }
      return this.isPaidHalfDay;
    },
    descriptionLabel(): string {
      return this.isWork ? '業務内容' : '備考';
    },
    descriptionItem(): any {
      return {
        ...this.descriptionItemBase,
        placeholder: this.isWork ? '業務内容を入力してください。' : '勤怠内容を入力してください。',
        required: this.isWork,
      };
    },
    selectedDateAsDate(): Date | undefined {
      if (!this.selectedDate) {
        return undefined;
      }

      const [y, m, d] = this.selectedDate.split('-').map(Number);
      return new Date(y, m - 1, d);
    },
    placeHolder(): string {
      if (!this.selectedDate) {
        return formatYmdSlash(new Date());
      }
      const [y, m, d] = this.selectedDate.split('-').map(Number);
      return formatYmdSlash(new Date(y, m - 1, d));
    },
    displayTaskOptions() {
      const base = (this.taskOptions || []).map(t => ({ ...t }));
      const exists = base.some(t => t.value === this.originalTaskId);

      if (!exists && this.originalTaskId != null && this.originalTaskName) {
        base.unshift({
          id: this.originalTaskId,
          value: this.originalTaskId,
          text: `${this.originalTaskName}（削除済み）`,
        });
      }
      return base;
    },
  },
  methods: {
    getMaxEndTime(): string | null {
      if (!this.dayEvents || this.dayEvents.length === 0) {
        return null;
      }

      const maxMinutes = Math.max(...this.dayEvents.filter(e => e.end).map(e => this.toMinutes(e.end)));

      const h = Math.floor(maxMinutes / 60)
        .toString()
        .padStart(2, '0');
      const m = (maxMinutes % 60).toString().padStart(2, '0');

      return `${h}:${m}`;
    },

    hasAnyAttendance(): boolean {
      return this.dayEvents.length > 0;
    },
    resetErrors() {
      this.timeError = '';
      this.periodError = '';
      this.descriptionError = '';
      this.taskError = '';
      this.timeOffKindError = '';
      // NewVariableInput 側に渡す error もクリア
      this.descriptionItemBase.error = '';
      this.formError = '';
    },
    datePickerFormat(date: Date): string {
      return formatYmdSlash(date);
    },
    validateTime() {
      const start = this.local.start;
      const end = this.local.end;
      // 時間入力をそもそも見せていないケース（全日有給など）はチェックしない
      if (!this.showTime) {
        this.timeError = '';
        return true;
      }

      if (!start && !end) {
        this.timeError = '開始時間と終了時間を入力してください。';
        return false;
      }

      if (!start) {
        this.timeError = '開始時間を入力してください。';
        return false;
      }

      if (!end) {
        this.timeError = '終了時間を入力してください。';
        return false;
      }

      const toMinutes = (time: string): number => {
        const [h, m] = time
          .trim()
          .split(':')
          .map(Number);
        return h * 60 + m;
      };

      const inputStart = toMinutes(start);
      const inputEnd = toMinutes(end);
      const min = toMinutes(this.minTime);
      const max = toMinutes(this.maxTime);

      if (inputStart < min || inputEnd > max) {
        this.timeError = `時間は ${this.minTime} ～ ${this.maxTime} の範囲で入力してください。`;
        return false;
      }

      // "HH:MM" 形式なら文字列比較でも前後関係は判定できる
      if (inputEnd <= inputStart) {
        this.timeError = '「開始時間」より「終了時間」が過去になっています。';
        return false;
      }
      // 既存の勤怠との重複チェック
      if (this.hasOverlapWithOthers(start, end)) {
        this.timeError = '他の勤怠と時間帯が重複しています。';
        return false;
      }
      this.timeError = '';
      return true;
    },
    // 期間バリデーション
    validatePeriod(mode = 'submit') {
      // 休暇以外のときはチェックしない＆エラーも消す
      if (!this.isTimeOffAbsence) {
        this.periodError = '';
        return true;
      }

      const start = this.local.timeOffStart;
      const end = this.local.timeOffEnd;

      // どちらか未入力
      if (!start || !end) {
        if (mode === 'submit') {
          this.periodError = '開始日と終了日を入力してください。';
          return false;
        } else {
          this.periodError = '';
          return true;
        }
      }

      const s = new Date(start).getTime();
      const e = new Date(end).getTime();

      if (e < s) {
        this.periodError = '「開始日」より「終了日」が過去になっています。';
        return false;
      }
      this.periodError = '';
      return true;
    },
    validateDescription() {
      // 出勤以外（欠勤/休暇など）はチェックしない
      if (!this.isWork) {
        return true;
      }

      validation.checkValue(this.local.description, this.descriptionItemBase);
      if (this.descriptionItemBase.error) {
        return false;
      }

      // 正常
      this.descriptionError = '';
      return true;
    },

    validateTask() {
      // 出勤以外はチェック不要
      if (!this.isWork) {
        this.taskError = '';
        return true;
      }

      if (!this.local.taskId) {
        this.taskError = '業務名を選択してください。';
        return false;
      }

      this.taskError = '';
      return true;
    },

    // "HH:MM" → 分へ
    toMinutes(hhmm: string): number {
      const [h, m] = hhmm.split(':').map(v => parseInt(v, 10));
      return h * 60 + (m || 0);
    },
    // 既存勤怠との重複判定
    hasOverlapWithOthers(start: string, end: string): boolean {
      const s = this.toMinutes(start);
      const e = this.toMinutes(end);

      return this.dayEvents.some((ev: any) => {
        if (!ev.start || !ev.end) {
          return false;
        }
        const es = this.toMinutes(ev.start);
        const ee = this.toMinutes(ev.end);

        // [s, e) と [es, ee) が重なるか
        return s < ee && e > es;
      });
    },
    validateAll(): boolean {
      const okTask = this.validateTask();
      const okDesc = this.validateDescription();
      const okTime = this.validateTime();
      const okPeriod = this.validatePeriod('submit');
      const okTimeOffKind = this.validateTimeOffKind();
      const okAbsence = this.validateAbsenceAvailability();
      return okTask && okDesc && okTime && okPeriod && okTimeOffKind && okAbsence;
    },
    submit(kind: 'update' | 'add-another') {
      if (!this.validateAll()) {
        return;
      }
      const payload = { ...this.local };
      this.$emit(kind === 'update' ? 'update' : 'add-another', payload);
    },
    onUpdate() {
      this.submit('update');
    },
    onAddAnother() {
      this.submit('add-another');
    },
    validateAbsenceAvailability() {
      if (this.local.kind !== LaborCalendarEventKind.ABSENCE) {
        return true;
      }
      if (
        (this.local.absenceKind === 'paidLeave' && this.local.paidLeaveKind === 'full-day') ||
        this.local.absenceKind === 'absence'
      ) {
        const hasAttendance = this.hasAnyAttendance();
        if (hasAttendance) {
          this.formError = '他の勤怠と期間が重複しています。';
          return false;
        }
      }
      return true;
    },
    validateTimeOffKind() {
      // 「欠勤」かつ「休暇」のときだけチェック
      if (!this.isTimeOffAbsence) {
        this.timeOffKindError = '';
        return true;
      }

      if (this.local.specialLeaveId === undefined) {
        this.timeOffKindError = '休暇区分を選択してください。';
        return false;
      }

      this.timeOffKindError = '';
      return true;
    },
  },
  mounted() {
    this.resetErrors();

    if (this.editForm) {
      return;
    }

    const maxEnd = this.getMaxEndTime();
    if (!maxEnd) {
      return;
    }

    this.local.start = maxEnd;

    let endMinutes = this.toMinutes(maxEnd) + 60;
    const maxAllowed = this.toMinutes(this.maxTime);

    if (endMinutes > maxAllowed) {
      endMinutes = maxAllowed;
    }

    const h = Math.floor(endMinutes / 60)
      .toString()
      .padStart(2, '0');
    const m = (endMinutes % 60).toString().padStart(2, '0');

    this.local.end = `${h}:${m}`;
  },
} as ThisTypedComponentOptionsWithRecordProps<Vue, Data, Methods, Computed, Props>);
